import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { MessageSquare, Send, Sparkles, ArrowRight, CheckCircle, Clock } from "lucide-react";
import { createTicket, listTickets } from "../data/demoStore";

export function Portal() {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [submitted, setSubmitted] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const canSubmit = text.trim().length > 0;
  const hasTickets = listTickets().length > 0;

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit || isSubmitting) return;

    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 800));

    const trimmedText = text.trim();
    // Pre-fill requester identity for demo
    const requester = {
      name: "Demo User",
      email: "demo@company.com",
      avatar: "DU",
      department: "Sales",
      manager: "Mike Torres",
    };

    const t = createTicket({
      subject: trimmedText.length > 60 ? trimmedText.slice(0, 60) + "..." : trimmedText,
      description: trimmedText,
      requester,
    });

    setSubmitted(t);
    setText("");
    setIsSubmitting(false);
  }

  function handleSubmitAnother() {
    setSubmitted(null);
  }

  // After submit, show only the status card (hide form)
  if (submitted) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
              IT Help Portal
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Your request has been submitted.</p>
          </div>
        </div>

        <Card className="border-green-200 bg-green-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <CardTitle className="text-lg text-green-900">Request Created</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 rounded-lg bg-white border border-green-200">
              <div className="flex items-center justify-between mb-3">
                <span className="text-sm font-mono text-muted-foreground">{submitted.id}</span>
                <Badge
                  variant="outline"
                  className={submitted.status === "pending_approval"
                    ? "bg-purple-100 text-purple-800 border-purple-200"
                    : "bg-blue-100 text-blue-800 border-blue-200"
                  }
                >
                  <Clock className="w-3 h-3 mr-1" />
                  {submitted.status === "pending_approval" ? "Awaiting Approval" : "In Progress"}
                </Badge>
              </div>
              <h3 className="text-sm font-medium text-foreground mb-2">{submitted.subject}</h3>
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-green-100">
                <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-medium text-primary">V</div>
                <p className="text-sm text-muted-foreground">
                  {submitted.status === "pending_approval"
                    ? "Requires manager approval. I've sent the request."
                    : "Got it! Working on this now."}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Button onClick={() => navigate(`/tickets/${submitted.id}`)}>
                View Details
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button variant="outline" onClick={handleSubmitAnother}>
                Submit Another
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="text-center pt-4">
          <p className="text-xs text-muted-foreground">
            Are you an IT agent?{" "}
            <Link to="/tickets" className="text-primary hover:underline">
              Open agent queue →
            </Link>
          </p>
        </div>
      </div>
    );
  }

  // Form view (before submit)
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-muted-foreground" />
            IT Help Portal
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Submit a request and get help from IT.</p>
        </div>
        {hasTickets && (
          <Button asChild variant="outline" size="sm">
            <Link to="/my-requests">My requests</Link>
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">What do you need help with?</CardTitle>
          <CardDescription>Describe your request and we'll route it to the right team.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="I need help with..."
              className="min-h-[120px] resize-none"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setText("I need access to Salesforce for my sales role.")}
                className="text-xs px-3 py-1.5 rounded-full border border-border bg-muted/30 hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
              >
                Request Salesforce access
              </button>
            </div>
            <div className="pt-2">
              <Button type="submit" disabled={!canSubmit || isSubmitting} className="w-full sm:w-auto">
                {isSubmitting ? (
                  <>Submitting...</>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <div className="text-center pt-4">
        <p className="text-xs text-muted-foreground">
          Are you an IT agent?{" "}
          <Link to="/tickets" className="text-primary hover:underline">
            Open agent queue →
          </Link>
        </p>
      </div>
    </div>
  );
}

export default Portal;
